/**
 * AdminLayout.js
 * Layout component for Admin Portal pages
 * Separate from main AppLayout - admin-specific navigation
 */

import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { LAYOUT_TOP_OFFSET } from '../config';
import colors from '../utils/adminColors';

/* Admin sidebar collapse button — show on hover via CSS */
const adminCollapseStyles = `
.admin-collapse-btn { opacity: 1; }
.admin-collapse-btn:hover { background: #F9FAFB !important; box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important; }
`;

/* Palette sourced from the shared admin token mirror (utils/adminColors, R6
   Lane 220) -- the second near-twin palette that used to live here is retired
   to the one token set. */

function AdminLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 1440);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setSidebarCollapsed(window.innerWidth < 1440);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', label: 'Dashboard', exact: true, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
    { path: '/admin/companies', label: 'Companies', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
    { path: '/admin/knowledge', label: 'Knowledge', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg> },
    { path: '/admin/users', label: 'Suppliers & Admins', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { path: '/admin/chat-outcomes', label: 'Chat Outcomes', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/><path d="M8 9h8"/><path d="M8 13h5"/></svg> },
    { path: '/admin/cron-runs', label: 'Cron Runs', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    { path: '/admin/demo-requests', label: 'Demo Requests', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"/><polyline points="4 7 12 13 20 7"/></svg> },
  ];

  return (
    <div className="portal-admin" style={{
      minHeight: `calc(100vh - ${LAYOUT_TOP_OFFSET}px)`,
      backgroundColor: colors.lightGray,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* Sidebar */}
      <aside className="admin-sidebar" style={{
        position: 'fixed',
        top: LAYOUT_TOP_OFFSET,
        left: 0,
        bottom: 0,
        width: sidebarCollapsed ? '72px' : '260px',
        backgroundColor: colors.deepBlue,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Logo Section */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-size-xl)',
            flexShrink: 0,
          }}>

          </div>
          {!sidebarCollapsed && (
            <div>
              <div style={{ color: colors.white, fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-md)' }}>
                Wingman
              </div>
              <div style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
                Admin
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `admin-nav-item${isActive ? ' admin-nav-active' : ''}`}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: sidebarCollapsed ? '12px' : '12px 16px',
                marginBottom: '4px',
                borderRadius: '8px',
                color: isActive ? colors.white : colors.textMuted,
                backgroundColor: isActive ? 'rgba(124, 58, 237, 0.2)' : undefined,
                textDecoration: 'none',
                fontSize: 'var(--font-size-base)',
                fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                transition: 'background-color 150ms ease, color 150ms ease',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              })}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', flexShrink: 0 }}>{item.icon}</span>
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse Toggle — edge chevron (Linear/Notion style) */}
        <button
          className="admin-collapse-btn"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{
            position: 'absolute',
            top: '50%',
            right: '-12px',
            transform: 'translateY(-50%)',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: '#6B7280',
            zIndex: 10,
            opacity: 1,
            transition: 'all 150ms ease',
            padding: 0,
          }}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? '\u203A' : '\u2039'}
        </button>
        <style>{adminCollapseStyles}</style>

        {/* Back to App Link */}
        <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <NavLink
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: sidebarCollapsed ? '12px' : '12px 16px',
              borderRadius: '8px',
              color: colors.textMuted,
              textDecoration: 'none',
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-lg)' }}></span>
            {!sidebarCollapsed && <span>Back to App</span>}
          </NavLink>
        </div>

        {/* User Section */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          {!sidebarCollapsed ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.white,
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
              }}>
                {user?.full_name?.charAt(0) || 'A'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: colors.white,
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {user?.full_name || 'Admin'}
                </div>
                <div style={{
                  color: colors.textMuted,
                  fontSize: 'var(--font-size-sm)',
                }}>
                  {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                </div>
              </div>
              <button
                onClick={handleLogout}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
                title="Log Out"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '6px',
                fontSize: 'var(--font-size-base)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Log Out"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="content-fade-in" style={{
        marginLeft: sidebarCollapsed ? '72px' : '260px',
        minHeight: `calc(100vh - ${LAYOUT_TOP_OFFSET}px)`,
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {children}
      </main>
    </div>
  );
}

export default AdminLayout;
